import { useEffect, useMemo, useState } from "react";
import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Home from "./pages/Home";
import EnquiryPage from "./pages/EnquiryPage";
import Careers from "./pages/Careers";
import Terms from "./pages/Terms";
import { ThemeContext } from "./ThemeContext";
import PageSeo from "./components/PageSeo";

function useRevealOnScroll() {
  const location = useLocation();
  useEffect(() => {
    // Slight delay to ensure new route content has mounted
    const timer = setTimeout(() => {
      const els = document.querySelectorAll(".reveal:not(.visible)");
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("visible");
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
      );
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.add("visible");
        } else {
          io.observe(el);
        }
      });
      return () => io.disconnect();
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);
}

function AppShell() {
  useRevealOnScroll();
  const location = useLocation();
  return (
    <>
      <PageSeo pathname={location.pathname} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/enquiry" element={<EnquiryPage />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/terms" element={<Terms />} />
      </Routes>
    </>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem("site-theme") || "dark";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem("site-theme", theme);
  }, [theme]);

  const themeValue = useMemo(
    () => ({
      theme,
      isDark: theme === "dark",
      toggleTheme: () =>
        setTheme((current) => (current === "dark" ? "light" : "dark")),
    }),
    [theme],
  );

  return (
    <div className="App theme-root min-h-screen">
      <ThemeContext.Provider value={themeValue}>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </ThemeContext.Provider>
      <Toaster
        theme={theme}
        position="bottom-right"
        toastOptions={{
          style: {
            background: "var(--toast-bg)",
            border: "1px solid var(--toast-border)",
            color: "var(--text-primary)",
            backdropFilter: "blur(16px)",
          },
        }}
      />
    </div>
  );
}

export default App;
